/**
 * Discover Contract Client
 * Supabase client functions for discovery queries
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { 
  DiscoverFilters, 
  FacetResponse, 
  FacetResponseV3,
  DiscoverDeal, 
  DiscoverDealV3,
  DiscoverResultsResponse,
  DiscoverResultsResponseV3,
  TaxonomyCoverage,
  CategoryItem,
  SubcategoryItem,
  TaxonomyItem,
  DealCardVM,
  DealDetailVM,
  DealViewerRole,
} from './types';

/**
 * Get discovery facets from the database (v2)
 */
export async function getDiscoverFacets(
  supabase: SupabaseClient,
  filters: DiscoverFilters
): Promise<FacetResponse> {
  const rpcFilters = {
    q: filters.q || '',
    subjects: Array.isArray(filters.subjects) ? filters.subjects : [],
    geo: Array.isArray(filters.geo) ? filters.geo : [],
    brand_categories: Array.isArray(filters.brand_categories) ? filters.brand_categories : [],
    psychographics: Array.isArray(filters.psychographics) ? filters.psychographics : [],
    brand_geographic_focus: Array.isArray(filters.brand_geographic_focus) ? filters.brand_geographic_focus : [],
  };

  const { data, error } = await supabase.rpc('rpc_get_discover_facets_v2', {
    filters: rpcFilters,
  });

  if (error) {
    console.error('[getDiscoverFacets] RPC error:', error);
    throw new Error(error.message);
  }

  const parsed = data as FacetResponse;
  return {
    subjects: parsed?.subjects || [],
    geo: parsed?.geo || [],
    brand_categories: parsed?.brand_categories || [],
    psychographics: parsed?.psychographics || [],
    brand_geographic_focus: parsed?.brand_geographic_focus || [],
    total: parsed?.total || 0,
  };
}

/**
 * Get discovery facets from the database (v3)
 */
export async function getDiscoverFacetsV3(
  supabase: SupabaseClient,
  filters: DiscoverFilters
): Promise<FacetResponseV3> {
  const rpcFilters = {
    q: filters.q || '',
    subjects: Array.isArray(filters.subjects) ? filters.subjects : [],
    geo: Array.isArray(filters.geo) ? filters.geo : [],
    brand_categories: Array.isArray(filters.brand_categories) ? filters.brand_categories : [],
    psychographics: Array.isArray(filters.psychographics) ? filters.psychographics : [],
    brand_geographic_focus: Array.isArray(filters.brand_geographic_focus) ? filters.brand_geographic_focus : [],
    industry_ids: Array.isArray(filters.industry_ids) ? filters.industry_ids : [],
    category_ids: Array.isArray(filters.category_ids) ? filters.category_ids : [],
    subcategory_ids: Array.isArray(filters.subcategory_ids) ? filters.subcategory_ids : [],
    hashtag_ids: Array.isArray(filters.hashtag_ids) ? filters.hashtag_ids : [],
  };

  const { data, error } = await supabase.rpc('rpc_get_discover_facets_v3', {
    filters: rpcFilters,
  });

  if (error) {
    console.error('[getDiscoverFacetsV3] RPC error:', error);
    throw new Error(error.message);
  }

  const parsed = data as FacetResponseV3;
  return {
    subjects: parsed?.subjects || [],
    geo: parsed?.geo || [],
    brand_categories: parsed?.brand_categories || [],
    psychographics: parsed?.psychographics || [],
    brand_geographic_focus: parsed?.brand_geographic_focus || [],
    industries: parsed?.industries || [],
    categories: parsed?.categories || [],
    subcategories: parsed?.subcategories || [],
    hashtags: parsed?.hashtags || [],
    total: parsed?.total || 0,
  };
}

/**
 * Get discovery results from discover_deals_v2 view
 */
export async function getDiscoverResults(
  supabase: SupabaseClient,
  filters: DiscoverFilters
): Promise<DiscoverResultsResponse> {
  let query = supabase.from('discover_deals_v2').select('*', { count: 'exact' });

  if (filters.q && filters.q.trim()) {
    const searchTerm = `%${filters.q.trim()}%`;
    query = query.or(`title.ilike.${searchTerm},description.ilike.${searchTerm},brand_name.ilike.${searchTerm}`);
  }

  if (filters.subjects.length > 0) query = query.overlaps('subject_filters', filters.subjects);
  if (filters.geo.length > 0) query = query.overlaps('geo_filters', filters.geo);
  if (filters.brand_categories.length > 0) query = query.overlaps('brand_categories', filters.brand_categories);
  if (filters.psychographics.length > 0) query = query.overlaps('brand_psychographics', filters.psychographics);
  if (filters.brand_geographic_focus.length > 0) query = query.overlaps('brand_geographic_focus', filters.brand_geographic_focus);

  switch (filters.sort) {
    case 'latest': query = query.order('created_at', { ascending: false, nullsFirst: false }); break;
    case 'price_asc': query = query.order('price_min', { ascending: true, nullsFirst: false }); break;
    case 'price_desc': query = query.order('price_max', { ascending: false, nullsFirst: false }); break;
    default: query = query.order('created_at', { ascending: false, nullsFirst: false }); break;
  }

  const from = (filters.page - 1) * filters.pageSize;
  const to = from + filters.pageSize - 1;
  query = query.range(from, to);

  const { data, error, count } = await query;

  if (error) {
    console.error('[getDiscoverResults] Query error:', error);
    throw new Error(error.message);
  }

  const items: DiscoverDeal[] = (data || []).map((row: Record<string, unknown>) => ({
    deal_id: row.deal_id as string,
    brand_id: row.brand_id as string,
    title: row.title as string,
    description: row.description as string | null,
    status: row.status as string,
    starts_at: row.starts_at as string | null,
    expires_at: row.expires_at as string | null,
    created_at: row.created_at as string | null,
    commission_rate: row.commission_rate as number,
    discount_value: row.discount_value as number,
    sale_cents: row.sale_cents as number | null,
    msrp_cents: row.msrp_cents as number | null,
    creator_commission_pct: row.creator_commission_pct as number | null,
    friend_discount_pct: row.friend_discount_pct as number | null,
    platform_fee_pct: row.platform_fee_pct as number | null,
    creator_bonus_cents: row.creator_bonus_cents as number | null,
    currency: row.currency as string | null,
    store_product_id: row.store_product_id as string | null,
    cover_image_url: row.cover_image_url as string | null,
    video_url: row.video_url as string | null,
    hero_image_variant: row.hero_image_variant as string | null,
    geo_filters: row.geo_filters as string[] | null,
    subject_filters: row.subject_filters as string[] | null,
    demo_filters: row.demo_filters as { age_min?: number; age_max?: number; genders?: string[] } | null,
    brand_name: row.brand_name as string,
    brand_avatar_url: row.brand_avatar_url as string | null,
    brand_categories: row.brand_categories as string[] | null,
    brand_psychographics: row.brand_psychographics as string[] | null,
    brand_geographic_focus: row.brand_geographic_focus as string[] | null,
    primary_product_id: row.primary_product_id as string | null,
    price_min: row.price_min as number | null,
    price_max: row.price_max as number | null,
    commission_percent: row.commission_percent_max as number | null,
    discount_percent: row.discount_percent_max as number | null,
    image_url: row.image_url_primary as string | null,
    product_count: (row.product_count as number) || 0,
    adopt_count: (row.adopt_count as number) || 0,
    latest_adopted_at: row.latest_adopted_at as string | null,
  }));

  return { items, total: count || items.length };
}

/**
 * Get discovery results from discover_deals_v3 view
 */
export async function getDiscoverResultsV3(
  supabase: SupabaseClient,
  filters: DiscoverFilters
): Promise<DiscoverResultsResponseV3> {
  let query = supabase.from('discover_deals_v3').select('*', { count: 'exact' });

  if (filters.q && filters.q.trim()) {
    const searchTerm = `%${filters.q.trim()}%`;
    query = query.or(`title.ilike.${searchTerm},description.ilike.${searchTerm},brand_name.ilike.${searchTerm}`);
  }

  if (filters.subjects.length > 0) query = query.overlaps('subject_filters', filters.subjects);
  if (filters.geo.length > 0) query = query.overlaps('geo_filters', filters.geo);
  if (filters.brand_categories.length > 0) query = query.overlaps('brand_categories', filters.brand_categories);
  if (filters.psychographics.length > 0) query = query.overlaps('brand_psychographics', filters.psychographics);
  if (filters.brand_geographic_focus.length > 0) query = query.overlaps('brand_geographic_focus', filters.brand_geographic_focus);

  switch (filters.sort) {
    case 'latest': query = query.order('created_at', { ascending: false, nullsFirst: false }); break;
    case 'price_asc': query = query.order('price_min', { ascending: true, nullsFirst: false }); break;
    case 'price_desc': query = query.order('price_max', { ascending: false, nullsFirst: false }); break;
    default: query = query.order('created_at', { ascending: false, nullsFirst: false }); break;
  }

  const pageMultiplier = (filters.industry_ids?.length ?? 0) > 0 ? 3 : 1;
  const from = 0;
  const to = (filters.page * filters.pageSize * pageMultiplier) - 1;
  query = query.range(from, Math.min(to, 999));

  const { data, error } = await query;

  if (error) {
    console.error('[getDiscoverResultsV3] Query error:', error);
    throw new Error(error.message);
  }

  let items: DiscoverDealV3[] = (data || []).map((row: Record<string, unknown>) => ({
    deal_id: row.deal_id as string,
    brand_id: row.brand_id as string,
    title: row.title as string,
    description: row.description as string | null,
    status: row.status as string,
    starts_at: row.starts_at as string | null,
    expires_at: row.expires_at as string | null,
    created_at: row.created_at as string | null,
    commission_rate: row.commission_rate as number,
    discount_value: row.discount_value as number,
    sale_cents: row.sale_cents as number | null,
    msrp_cents: row.msrp_cents as number | null,
    creator_commission_pct: row.creator_commission_pct as number | null,
    friend_discount_pct: row.friend_discount_pct as number | null,
    platform_fee_pct: row.platform_fee_pct as number | null,
    creator_bonus_cents: row.creator_bonus_cents as number | null,
    currency: row.currency as string | null,
    store_product_id: row.store_product_id as string | null,
    cover_image_url: row.cover_image_url as string | null,
    video_url: row.video_url as string | null,
    hero_image_variant: row.hero_image_variant as string | null,
    geo_filters: row.geo_filters as string[] | null,
    subject_filters: row.subject_filters as string[] | null,
    demo_filters: row.demo_filters as { age_min?: number; age_max?: number; genders?: string[] } | null,
    brand_name: row.brand_name as string,
    brand_avatar_url: row.brand_avatar_url as string | null,
    brand_handle: (row.brand_handle as string | null) ?? null,
    brand_categories: row.brand_categories as string[] | null,
    brand_psychographics: row.brand_psychographics as string[] | null,
    brand_geographic_focus: row.brand_geographic_focus as string[] | null,
    primary_product_id: row.primary_product_id as string | null,
    price_min: row.price_min as number | null,
    price_max: row.price_max as number | null,
    commission_percent: row.commission_percent_max as number | null,
    discount_percent: row.discount_percent_max as number | null,
    image_url: row.image_url_primary as string | null,
    product_count: (row.product_count as number) || 0,
    adopt_count: (row.adopt_count as number) || 0,
    latest_adopted_at: row.latest_adopted_at as string | null,
    industries: (row.industries as TaxonomyItem[]) || [],
    categories: (row.categories as CategoryItem[]) || [],
    subcategories: (row.subcategories as SubcategoryItem[]) || [],
    custom_subcategories: (row.custom_subcategories as any[]) || [],
    hashtags: (row.hashtags as any[]) || [],
  }));

  if (filters.industry_ids && filters.industry_ids.length > 0) {
    items = items.filter(deal => deal.categories.some(cat => filters.industry_ids!.includes(cat.industry_id)));
  }
  if (filters.category_ids && filters.category_ids.length > 0) {
    items = items.filter(deal => deal.categories.some(cat => filters.category_ids!.includes(cat.id)));
  }
  if (filters.subcategory_ids && filters.subcategory_ids.length > 0) {
    items = items.filter(deal => deal.subcategories.some(sub => filters.subcategory_ids!.includes(sub.id)));
  }

  const startIdx = (filters.page - 1) * filters.pageSize;
  const paginatedItems = items.slice(startIdx, startIdx + filters.pageSize);

  return { items: paginatedItems, total: items.length };
}

/**
 * Get user's adopted deal IDs for filtering out already-joined deals
 */
export async function getUserAdoptedDealIds(
  supabase: SupabaseClient,
  katalogId: string
): Promise<Set<string>> {
  const { data } = await supabase
    .from('deal_katalogs')
    .select('deal_id')
    .eq('katalog_id', katalogId)
    .eq('status', 'active');

  return new Set((data || []).map(row => row.deal_id));
}

/**
 * Get user's katalog ID from auth user
 */
export async function getUserKatalogId(
  supabase: SupabaseClient,
  authUserId: string
): Promise<string | null> {
  const { data } = await supabase
    .from('katalog')
    .select('id')
    .eq('owner_user_id', authUserId)
    .maybeSingle();

  return data?.id || null;
}

/** Parameters for scoped coverage query - re-export from types */
import type { TaxonomyCoverageParams } from './types';
export type { TaxonomyCoverageParams };

/** Empty/error coverage result */
const EMPTY_COVERAGE: TaxonomyCoverage = {
  total_deals: 0,
  deals_with_taxonomy: 0,
  deals_without_taxonomy: 0,
  coverage_percent: null,
  deal_count: 0,
  min_price_amount: null,
  max_price_amount: null,
  min_discount_amount: null,
  max_discount_amount: null,
  min_commission_amount: null,
  max_commission_amount: null,
};

/**
 * Get taxonomy coverage statistics using RPC
 */
export async function getTaxonomyCoverage(
  supabase: SupabaseClient,
  params?: TaxonomyCoverageParams
): Promise<TaxonomyCoverage> {
  try {
    const rpcArgs: Record<string, unknown> = {};
    
    if (params?.industry_ids?.length) rpcArgs.p_industry_ids = params.industry_ids;
    if (params?.category_ids?.length) rpcArgs.p_category_ids = params.category_ids;
    if (params?.subcategory_ids?.length) rpcArgs.p_subcategory_ids = params.subcategory_ids;
    if (params?.price_min !== undefined) rpcArgs.p_price_min = params.price_min;
    if (params?.price_max !== undefined) rpcArgs.p_price_max = params.price_max;
    if (params?.discount_min !== undefined) rpcArgs.p_discount_min = params.discount_min;
    if (params?.discount_max !== undefined) rpcArgs.p_discount_max = params.discount_max;
    if (params?.commission_min !== undefined) rpcArgs.p_commission_min = params.commission_min;
    if (params?.commission_max !== undefined) rpcArgs.p_commission_max = params.commission_max;

    const { data, error } = await supabase.rpc('rpc_get_taxonomy_coverage_deals', rpcArgs);

    if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') {
      console.log('[COVERAGE]', { params, rpcArgs, data, error });
    }

    if (error) {
      console.error('[COVERAGE_ERROR]', error);
      return EMPTY_COVERAGE;
    }

    const row = Array.isArray(data) ? data[0] : data;

    return {
      total_deals: row?.total_deals ?? 0,
      deals_with_taxonomy: row?.deals_with_taxonomy ?? 0,
      deals_without_taxonomy: row?.deals_without_taxonomy ?? 0,
      coverage_percent: row?.coverage_percent ?? null,
      deal_count: row?.deal_count ?? 0,
      min_price_amount: row?.min_price_amount ?? null,
      max_price_amount: row?.max_price_amount ?? null,
      min_discount_amount: row?.min_discount_amount ?? null,
      max_discount_amount: row?.max_discount_amount ?? null,
      min_commission_amount: row?.min_commission_amount ?? null,
      max_commission_amount: row?.max_commission_amount ?? null,
    };
  } catch (err) {
    console.error('[COVERAGE_ERROR] Unexpected:', err);
    return EMPTY_COVERAGE;
  }
}

/**
 * Check if URL filters contain v3 taxonomy params
 */
export function hasV3Filters(filters: DiscoverFilters): boolean {
  return (
    (filters.industry_ids?.length ?? 0) > 0 ||
    (filters.category_ids?.length ?? 0) > 0 ||
    (filters.subcategory_ids?.length ?? 0) > 0
  );
}

/**
 * Strip v3 taxonomy filters from filters object (for fallback to v2)
 */
export function stripV3Filters(filters: DiscoverFilters): DiscoverFilters {
  const { industry_ids, category_ids, subcategory_ids, ...rest } = filters;
  return {
    ...rest,
    industry_ids: [],
    category_ids: [],
    subcategory_ids: [],
  };
}

/**
 * Determine whether v3 mode should be used
 */
export interface V3GateResult {
  enabled: boolean;
  requested: boolean;
  coverage: TaxonomyCoverage;
}

export async function getV3GateStatus(
  supabase: SupabaseClient,
  filters: DiscoverFilters,
  options: {
    force?: boolean;
    threshold?: number;
  } = {}
): Promise<V3GateResult> {
  const { force = false, threshold = 80 } = options;
  
  const coverage = await getTaxonomyCoverage(supabase);
  const requested = hasV3Filters(filters);
  
  const enabled = force || (coverage.coverage_percent !== null && coverage.coverage_percent >= threshold);
  
  return { enabled, requested, coverage };
}

/**
 * Normalized industry option shape
 */
export interface NormalizedIndustryOption {
  id: string;
  label: string;
  slug?: string;
  count: number | null;
}

export function normalizeIndustryOption(
  industry: { id: string; label: string; slug?: string; count?: number | null }
): NormalizedIndustryOption {
  return {
    id: industry.id,
    label: industry.label,
    slug: industry.slug,
    count: industry.count ?? null,
  };
}

/**
 * Fetch all industry options from taxonomy_industries table
 */
export async function getAllIndustries(
  supabase: SupabaseClient
): Promise<NormalizedIndustryOption[]> {
  const { data, error } = await supabase
    .from('taxonomy_industries')
    .select('id, label, slug')
    .order('label');
  
  if (error) {
    console.error('[getAllIndustries] Error:', error);
    return [];
  }
  
  return (data || []).map(normalizeIndustryOption);
}

/**
 * @deprecated Use getV3GateStatus for full gate logic
 */
export async function shouldUseV3(
  supabase: SupabaseClient,
  options: {
    force?: boolean;
    coveragePercent?: number | null;
    threshold?: number;
  } = {}
): Promise<boolean> {
  const { force = false, threshold = 80 } = options;
  
  if (force) return true;
  
  let coverage = options.coveragePercent;
  if (coverage === undefined) {
    const stats = await getTaxonomyCoverage(supabase);
    coverage = stats.coverage_percent;
  }
  
  return coverage !== null && coverage >= threshold;
}

// ============================================================
// Phase 2+3: Role-aware feed & detail client functions
// ============================================================

/**
 * Fetch customer-facing katalog feed via get_katalog_feed_v1 RPC.
 * Returns DealCardVM[] with customer-only pricing (no earnings/commission).
 */
export async function getKatalogFeedV1(
  supabase: SupabaseClient,
  handle: string,
  viewerRole: DealViewerRole = 'customer',
  inviteToken?: string | null,
): Promise<{ items: DealCardVM[]; total: number }> {
  const { data, error } = await supabase.rpc('get_katalog_feed_v1', {
    p_handle: handle,
    p_viewer_role: viewerRole,
    p_invite_token: inviteToken || null,
  });

  if (error) {
    console.error('[getKatalogFeedV1] RPC error:', error);
    return { items: [], total: 0 };
  }

  return {
    items: data?.items || [],
    total: data?.total || 0,
  };
}

/**
 * Fetch creator-facing discover feed via get_discover_feed_v1 RPC.
 * Returns DealCardVM[] with creator pricing (includes earnings/commission).
 */
export async function getDiscoverFeedV1(
  supabase: SupabaseClient,
  viewerRole: DealViewerRole = 'creator',
  userId?: string | null,
  limit: number = 40,
): Promise<{ items: DealCardVM[]; total: number }> {
  const { data, error } = await supabase.rpc('get_discover_feed_v1', {
    p_viewer_role: viewerRole,
    p_user_id: userId || null,
    p_limit: limit,
  });

  if (error) {
    console.error('[getDiscoverFeedV1] RPC error:', error);
    return { items: [], total: 0 };
  }

  return {
    items: data?.items || [],
    total: data?.total || 0,
  };
}

/**
 * Fetch deal detail via get_deal_detail_v1 RPC.
 * Returns DealDetailVM with role-aware pricing.
 * Customer role: NO earnings/commission/payout fields.
 */
export async function getDealDetailV1(
  supabase: SupabaseClient,
  dealId: string,
  viewerRole: DealViewerRole = 'customer',
  creatorUserId?: string | null,
): Promise<DealDetailVM | null> {
  const { data, error } = await supabase.rpc('get_deal_detail_v1', {
    p_deal_id: dealId,
    p_viewer_role: viewerRole,
    p_creator_user_id: creatorUserId || null,
  });

  if (error) {
    console.error('[getDealDetailV1] RPC error:', error);
    return null;
  }

  return data as unknown as DealDetailVM | null;
}
